import { BaseTableColumn } from '@/components/BaseTable'
import type { BaseLineItem, BasePriceItem } from 'api-price'
import type { PriceFundingListTable } from 'api-fundraising'
import { useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import Currency from './Currency'
import Price from './Price'
import { useStore } from '@/store/index'
import { HOME_PAGE_URL } from '@/constants/index'
import { useAdapter } from '@/components/Adapter'
import { useMemoizedFn, useUpdateEffect } from 'ahooks'
import Change from './Change'
import Volume from './Volume'
import MarketCap from './MarketCap'
import Chart from './Chart'
import Action from './Action'
import TitleSelect from './TitleSelect'
import dayjs from 'dayjs'
import BaseTooltip from '@/components/BaseTooltip'
import { eventTracker } from '@/utils/collectEvent'
import { Box, Flex } from '@mantine/core'
import { formatAddString, stopEvent } from '@/utils/tools'
import BaseRiseColor from '@/components/BaseRiseColor'
import useRateChange from '@/hooks/useRateChange'
import dynamic from 'next/dynamic'
import BaseImage from '@/components/BaseImage'
import useLangPrefix from '@/hooks/useLangPrefix'
import { PriceTableItemProps } from '..'
import { openConfirmModal, Tag } from '@gate/gui'
import FundraisingSelect from './FundraisingSelect'
import FundraisingAmount from './FundraisingAmount'
import FundraisingInvestors from './FundraisingInvestors'
import { useRouter } from 'next/router'
import { OverflowHideStyled } from './Action/style'
import FundraisingTags from './FundraisingTags'

// ! 服务端拿不到色值 使用 themevar
const GainersLosersCount = dynamic(() => import('./Sectors/LosersCount'), { ssr: false })

interface IBasePriceItem extends BasePriceItem {
  [key: string]: any
}

export interface IColumnsProps {
  lineLoading: boolean
  lineData?: Record<string, BaseLineItem[]>
  onSelectChange: (value: string | null) => void
  onCollect: (item: BasePriceItem) => void
  selectValue: string
  isCollect: boolean
  onTimeEnd: (item: BasePriceItem) => void
  onFundraisingSelectChange: (type: string, value: string) => void
}

const useColumns = ({
  isCollect,
  selectValue,
  lineLoading,
  lineData,
  onSelectChange,
  onCollect,
  onTimeEnd,
  onChangeTab,
  onFundraisingSelectChange,
}: IColumnsProps & PriceTableItemProps) => {
  const { locale } = useRouter()
  const { t } = useTranslation()
  const { size } = useAdapter()
  const { priceTab, setPriceTab, setTitleTab, fundraising, titleTab } = useStore().baseStore
  const isNew = priceTab === HOME_PAGE_URL.New
  const { formatRateChangePrice } = useRateChange()
  const { langPrefix } = useLangPrefix()
  const [fundraisingSelect, setFundraisingSelect] = useState({
    rounds: ' ',
    tag: ' ',
    investor: ' ',
    amount_start: ' ',
    amount_end: ' ',
    min: '',
    max: '',
    isInput: false,
  })

  useUpdateEffect(() => {
    setFundraisingSelect({
      rounds: ' ',
      tag: ' ',
      investor: ' ',
      amount_start: ' ',
      amount_end: ' ',
      min: '',
      max: '',
      isInput: false,
    })
  }, [titleTab])

  const colSpanNum = useMemoizedFn(() => {
    if (size === 'xs') return 1
    if (size === 'sm') return 2
    if (size === 'md') return 3
    return 5
  })

  const sectorsColumns = useMemo<BaseTableColumn<any>[]>(() => {
    const Currency: BaseTableColumn<IBasePriceItem> = {
      width: '18%',
      fixed: true,
      title: t('P.Name'),
      render({ lang, category_id }) {
        return (
          <a
            onClick={async e => {
              stopEvent(e)
              eventTracker('price_sectors_click', {
                button_name: category_id,
              })
              await setTitleTab(HOME_PAGE_URL.Market)
              await window.history.replaceState(null, '', `${langPrefix}/price/view/${category_id}`)
              await setPriceTab(category_id as any)
              onChangeTab(HOME_PAGE_URL.Others, category_id)
            }}
            className="text-1 font-500 size-16"
            href={`${langPrefix}/price/view/${category_id}`}
          >
            {lang?.length ? lang : '--'}
          </a>
        )
      },
    }
    const AllPercent: BaseTableColumn<IBasePriceItem> = {
      width: '16%',
      title: '24H %',
      sort: { allPercent: ['asc', 'desc'] },
      render({ allPercent }) {
        return (
          <BaseRiseColor value={allPercent}>
            <span className="size-16 font-500">{formatAddString(allPercent)}</span>
          </BaseRiseColor>
        )
      },
    }

    const CurrencyAllPercent: BaseTableColumn<IBasePriceItem> = {
      width: '20%',
      fixed: true,
      title: t('P.Name'),
      render({ lang, category_id }) {
        return (
          <Box
            component="a"
            className="text-1 font-500 size-16"
            pb={4}
            display={'block'}
            href={`${langPrefix}/price/view/${category_id}`}
            onClick={async e => {
              stopEvent(e)
              eventTracker('price_sectors_click', {
                button_name: category_id,
              })
              await setTitleTab(HOME_PAGE_URL.Market)
              await window.history.replaceState(null, '', `${langPrefix}/price/view/${category_id}`)
              await setPriceTab(category_id as any)
              onChangeTab(HOME_PAGE_URL.Others, category_id)
            }}
          >
            {lang?.length ? lang : '--'}
          </Box>
        )
      },
    }

    const TopGainers: BaseTableColumn<IBasePriceItem> = {
      width: '16%',
      title: t('P.TopGainers'),
      render({ change, logo_url, currency, crypto }) {
        return (
          <Flex gap={8} align="center">
            <BaseImage
              className=""
              width={28}
              height={28}
              alt={crypto}
              src={logo_url ?? `/images/coin_icon/64/${(crypto ?? '').toLowerCase()}.png`}
              loading="eager"
              style={{ borderRadius: '50%' }}
            />

            <Flex direction={'column'} gap={4} align="flex-start">
              <Box>
                <span className="text-1 size-16 font-500 nowarp">{crypto ?? '--'}</span>{' '}
                <span className="text-3 size-14 font-400 nowarp">{currency}</span>
              </Box>
              <BaseRiseColor value={change}>
                <span className="size-16 font-500 nowarp">{formatAddString(change)}</span>
              </BaseRiseColor>
            </Flex>
          </Flex>
        )
      },
    }
    const Mcap: BaseTableColumn<IBasePriceItem> = {
      width: '10%',
      title: t('P.Mcap'),
      render({ allMarket }) {
        return (
          <span className="size-16 font-500 nowarp">
            {formatRateChangePrice(allMarket, { isThousands: true, isUnit: true })}
          </span>
        )
      },
    }
    const Volume24H: BaseTableColumn<IBasePriceItem> = {
      width: '10%',
      title: t('P.Volume'),
      render({ allVolume }) {
        return (
          <span className="size-16 font-500 nowarp">
            {formatRateChangePrice(allVolume, { isThousands: true, isUnit: true })}
          </span>
        )
      },
    }

    const McapVolume24H: BaseTableColumn<IBasePriceItem> = {
      width: '20%',
      title: t('P.Volume') + '/' + t('P.Mcap'),
      render({ allVolume, allMarket }) {
        return (
          <>
            <span className="size-16 font-500 nowarp" style={{ display: 'block', paddingBottom: '4px' }}>
              {formatRateChangePrice(allVolume, { isThousands: true, isUnit: true })}
            </span>
            <span className="size-16 font-500 nowarp" style={{ display: 'block' }}>
              {formatRateChangePrice(allMarket, { isThousands: true, isUnit: true })}
            </span>
          </>
        )
      },
    }

    const GainersLosers: BaseTableColumn<IBasePriceItem> = {
      width: '30%',
      title: t('P.GLC'),
      render({ upCount, downCount }) {
        return <GainersLosersCount upCount={upCount} downCount={downCount} />
      },
    }

    if (size === 'xs') {
      return [
        { ...CurrencyAllPercent, width: '40%' },
        { ...AllPercent, width: '30%' },
        { ...GainersLosers, width: '30%' },
      ]
    }
    if (size === 'sm') {
      return [
        { ...CurrencyAllPercent, width: '20%' },
        { ...AllPercent, width: '20%' },
        { ...TopGainers, width: '20%' },
        { ...McapVolume24H, width: '20%' },
        { ...GainersLosers, width: '20%' },
      ]
    }
    if (size === 'md') {
      return [
        { ...CurrencyAllPercent, width: '20%' },
        { ...AllPercent, width: '16%' },
        { ...TopGainers, width: '20%' },
        { ...Mcap, width: '13%' },
        { ...Volume24H, width: '13%' },
        { ...GainersLosers, width: '20%' },
      ]
    }

    return [Currency, AllPercent, TopGainers, Mcap, Volume24H, GainersLosers]
  }, [formatRateChangePrice, langPrefix, onChangeTab, setPriceTab, setTitleTab, size, t])

  const columns = useMemo<BaseTableColumn<BasePriceItem>[]>(() => {
    if (size === 'xs') {
      return [
        {
          width: '40%',
          colSpan: () => 1,
          fixed: true,
          title: t('P.Currency'),
          render(props) {
            return <Currency onCollect={onCollect} isCollect={isCollect} {...props} />
          },
        },
        {
          width: '40%',
          title: t('P.Price'),
          sort: isNew ? undefined : { price: ['asc', 'desc'] },
          colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? colSpanNum() : 1),
          render(props) {
            return <Price onTimeEnd={onTimeEnd} {...props} />
          },
        },
        {
          width: '20%',
          title: t('P.Action'),
          colSpan: () => 1,
          render(props) {
            return <Action {...props} />
          },
        },
      ]
    }

    if (size === 'sm') {
      return [
        {
          width: '35%',
          colSpan: () => 1,
          fixed: true,
          title: t('P.Currency'),
          render(props) {
            return <Currency onCollect={onCollect} isCollect={isCollect} {...props} />
          },
        },
        {
          width: '22%',
          title: t('P.Price'),
          sort: isNew ? undefined : { price: ['asc', 'desc'] },
          colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? colSpanNum() : 1),
          render(props) {
            return <Price onTimeEnd={onTimeEnd} {...props} />
          },
        },
        {
          width: '23%',
          title: t('%'),
          sort: isNew ? undefined : { dimension_24h: ['asc', 'desc'] },
          colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? 0 : 1),
          extraTitle: <TitleSelect value={selectValue} onChange={onSelectChange} />,
          render(props) {
            return <Change {...props} />
          },
        },
        {
          width: '20%',
          title: t('P.Action'),
          colSpan: () => 1,
          render(props) {
            return <Action {...props} />
          },
        },
      ]
    }

    if (size === 'md') {
      return [
        {
          width: '30%',
          colSpan: () => 1,
          fixed: true,
          title: t('P.Currency'),
          render(props) {
            return <Currency onCollect={onCollect} isCollect={isCollect} {...props} />
          },
        },
        {
          width: '19%',
          title: t('P.Price'),
          sort: isNew ? undefined : { price: ['asc', 'desc'] },
          colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? colSpanNum() : 1),
          render(props) {
            return <Price onTimeEnd={onTimeEnd} {...props} />
          },
        },
        {
          width: '19%',
          title: t('%'),
          sort: isNew ? undefined : { dimension_24h: ['asc', 'desc'] },
          colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? 0 : 1),
          extraTitle: <TitleSelect value={selectValue} onChange={onSelectChange} />,
          render(props) {
            return <Change {...props} />
          },
        },

        {
          width: '12%',
          title: t('P.Chart'),
          colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? 0 : 1),
          render(props) {
            return <Chart lineLoading={lineLoading} lineData={lineData} {...props} />
          },
        },
        {
          width: '20%',
          title: t('P.Action'),
          colSpan: () => 1,
          render(props) {
            return <Action {...props} />
          },
        },
      ]
    }

    return [
      {
        width: '20%',
        colSpan: () => 1,
        fixed: true,
        title: t('P.Currency'),
        render(props) {
          return <Currency onCollect={onCollect} isCollect={isCollect} {...props} />
        },
      },
      {
        width: '10%',
        title: t('P.Price'),
        sort: isNew ? undefined : { price: ['asc', 'desc'] },
        colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? colSpanNum() : 1),
        render(props) {
          return <Price onTimeEnd={onTimeEnd} {...props} />
        },
      },
      {
        width: '14%',
        title: t('%'),
        sort: isNew ? undefined : { dimension_24h: ['asc', 'desc'] },
        colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? 0 : 1),
        extraTitle: <TitleSelect value={selectValue} onChange={onSelectChange} />,
        render(props) {
          return <Change {...props} />
        },
      },
      {
        width: '16%',
        title: t('P.Volume'),
        sort: isNew ? undefined : { volume_24h: ['asc', 'desc'] },
        colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? 0 : 1),
        render(props) {
          return <Volume {...props} />
        },
      },
      {
        width: '16%',
        title: t('P.MC', { index: '' }),
        sort: isNew ? undefined : { circulate_value: ['asc', 'desc'] },
        colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? 0 : 1),
        render(props) {
          return <MarketCap {...props} />
        },
      },
      {
        width: '120px',
        title: t('P.Chart'),
        colSpan: item => (item?.time && !dayjs().isAfter(dayjs((item?.time ?? 0) * 1000 + 1)) ? 0 : 1),
        render(props) {
          return <Chart lineLoading={lineLoading} lineData={lineData} {...props} />
        },
      },
      {
        width: 'clac(24% - 120px)',
        title: t('P.Action'),
        colSpan: () => 1,
        render(props) {
          return <Action {...props} />
        },
      },
    ]
  }, [size, t, isNew, selectValue, onSelectChange, onCollect, isCollect, colSpanNum, onTimeEnd, lineLoading, lineData])

  const fundraisingColumns = useMemo<BaseTableColumn<PriceFundingListTable>[]>(() => {
    return [
      {
        width: '15%',
        fixed: true,
        title: t('p.project.name', { ns: 'fundraising' }),
        render(props) {
          return <Currency isFundraising={true} onCollect={onCollect} isCollect={isCollect} {...props} />
        },
      },
      {
        width: '10%',
        title: t('p.funding', { ns: 'fundraising' }),
        renderTitle(title) {
          return (
            <Flex w="100%" justify="flex-start">
              <FundraisingSelect
                isMultiple={false}
                title={title}
                data={fundraising?.roundsSelect}
                value={fundraisingSelect.rounds}
                onChange={value => {
                  onFundraisingSelectChange('rounds', value)
                  setFundraisingSelect(params => ({
                    ...params,
                    rounds: value,
                  }))
                }}
              />
            </Flex>
          )
        },
        render(props) {
          return <span className="text-1 font-500 size-16">{props?.rounds_name ?? '--'}</span>
        },
      },
      {
        width: '20%',
        title: t('p.tags', { ns: 'fundraising' }),
        renderTitle(title) {
          return (
            <Flex w="100%" justify="flex-start">
              <FundraisingSelect
                title={title}
                data={fundraising?.tagSelect ?? ' '}
                value={fundraisingSelect.tag}
                onChange={value => {
                  onFundraisingSelectChange('tag', value)
                  setFundraisingSelect(params => ({
                    ...params,
                    tag: value,
                  }))
                }}
              />
            </Flex>
          )
        },
        render(props) {
          console.log('render', props)
          return <FundraisingTags detail={props?.detail ?? []} />
        },
      },
      {
        width: '15%',
        fixed: true,
        title: t('p.valuation', { ns: 'fundraising' }),
        render(props) {
          return (
            <span className="text-1 size-16 font-500">
              {formatRateChangePrice(props?.valuation_us ?? '', { isThousands: true })}
            </span>
          )
        },
      },
      {
        width: 'calc(15% - 16px)',
        title: t('p.amount', { ns: 'fundraising' }),
        renderTitle(title) {
          return (
            <FundraisingAmount
              title={title}
              fundraisingSelect={fundraisingSelect}
              setFundraisingSelect={setFundraisingSelect}
              onFundraisingSelectChange={onFundraisingSelectChange}
            />
          )
        },
        render(props) {
          return <Price isFundraising={true} {...props} />
        },
      },
      {
        width: 'calc(25% - 80px)',
        title: t('p.date', { ns: 'fundraising' }),
        sort: { publish_date: ['asc', 'desc'] },
        render(props) {
          return (
            <span className="text-1 font-500 size-16" style={{ paddingRight: '16px' }}>
              {props?.publish_date ? dayjs(props?.publish_date).format('YYYY-MM-DD') : '--'}
            </span>
          )
        },
      },
      {
        width: '60px',
        title: (
          <Flex align="center" gap={4}>
            {t('p.investors', { ns: 'fundraising' }) as string}{' '}
            <BaseTooltip isToolTipState label={t('p.first.investors', { ns: 'fundraising' })}>
              <svg className="iconpark-icon">
                <use href="#v31-16px-line-guanyuwomen"></use>
              </svg>
            </BaseTooltip>
          </Flex>
        ),

        renderTitle(title) {
          return (
            <FundraisingInvestors
              justify="flex-start"
              title={title}
              fundraisingSelect={fundraisingSelect}
              setFundraisingSelect={setFundraisingSelect}
              onFundraisingSelectChange={onFundraisingSelectChange}
            />
          )
        },
        render(props) {
          return <Action isFundraising={true} {...props} />
        },
      },
    ] as BaseTableColumn<any>[]
  }, [
    formatRateChangePrice,
    fundraising?.roundsSelect,
    fundraising?.tagSelect,
    fundraisingSelect,
    isCollect,
    onCollect,
    onFundraisingSelectChange,
    t,
  ])

  return { columns, sectorsColumns, fundraisingColumns }
}

export default useColumns
